import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUrlString } from '../../hooks/useUrlState';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation, confirmAction } from '../../lib/confirm';
import {
  Card, Button, Select, Input, Tag, Row, Col, StatCard, Modal, Drawer, Space, Spinner, Result, Tooltip,
  Upload, Popover, MenuItem, message, theme, Alert,
} from '../../ui';
import { Upload as UploadIcon, Download, Check, MoreHorizontal, AlertTriangle, FileSpreadsheet, Landmark, ArrowRight, ChevronRight, ChevronDown, Trash2, Clock, Eye, PencilLine } from 'lucide-react';
import { request } from '../../api/client';
import { ExportMenu } from '../../components/ExportMenu';
import { downloadCsv, downloadBinary } from '../../lib/downloadCsv';

/** Excel MIME — a blob saved with the wrong type is what makes a workbook "download but not open". */
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
import { usePermission } from '../../store/auth';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';
import { RetailerLink, OrderLink, UserLink } from '../../components/EntityLinks';
import { formatIstDate } from '../../lib/time';
import { customerOption, CUSTOMER_SELECT_PROPS } from '../../components/CustomerOption';
import { useRetailerSearch } from '../../hooks/useRetailerSearch';

const inr = (v: any) => {
  const n = Math.round(Number(v ?? 0) * 100) / 100;
  const paise = Math.round(n * 100) % 100 !== 0;
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: paise ? 2 : 0, maximumFractionDigits: 2 })}`;
};

/** Row states, with the wording an accounts user actually uses. */
const STATUS: Record<string, { label: string; tone: any }> = {
  UNMATCHED: { label: 'Unmatched', tone: 'default' },
  EXACT_MATCH: { label: 'Exact match', tone: 'green' },
  POSSIBLE_MATCH: { label: 'Possible match', tone: 'gold' },
  CONFLICT: { label: 'Conflict', tone: 'red' },
  BANK_ONLY: { label: 'Bank only', tone: 'blue' },
  MANUAL_MATCH: { label: 'Matched manually', tone: 'green' },
  CORRECTED: { label: 'Corrected', tone: 'green' },
  REVIEWED: { label: 'Reviewed', tone: 'default' },
  IGNORED: { label: 'Ignored', tone: 'default' },
};
/**
 * What is still stopping completion, in the reviewer's words. The counts come straight from the
 * server's own `blockers`, so this sentence and the backend's refusal always say the same thing.
 */
/**
 * Why Complete is not available yet, in the reviewer's own terms. Shown NEXT TO the button, not only
 * in a tooltip: a disabled button with the explanation hidden behind a hover reads as a broken page.
 */
function blockerText(b: any): string {
  if (!b) return '';
  const parts: string[] = [];
  if (b.undecidedRows) parts.push(`${b.undecidedRows} statement transaction${b.undecidedRows === 1 ? '' : 's'} still to decide`);
  if (b.unacceptedPossible) parts.push(`${b.unacceptedPossible} suggested match${b.unacceptedPossible === 1 ? '' : 'es'} to accept or reject`);
  if (b.conflicts) parts.push(`${b.conflicts} conflict${b.conflicts === 1 ? '' : 's'} to resolve`);
  if (b.unreviewedBankOnly) parts.push(`${b.unreviewedBankOnly} bank-only transaction${b.unreviewedBankOnly === 1 ? '' : 's'} to mark reviewed`);
  if (b.unreviewedErpOnly) parts.push(`${b.unreviewedErpOnly} ERP entr${b.unreviewedErpOnly === 1 ? 'y' : 'ies'} the bank never showed`);
  return parts.length ? `Outstanding: ${parts.join(' · ')}` : '';
}

/**
 * How far the statement's own running balance corroborated the way we read the file. A wrong mapping
 * is usually *plausible* — this is the only thing that catches it before the numbers are trusted.
 */
const PARSE_CHECK: Record<string, { tone: any; label: string; cls: string }> = {
  OK: { tone: 'green', label: 'Reading verified', cls: 'border-brand-500/30 bg-brand-500/10 text-brand-700 dark:text-brand-300' },
  PARTIAL: { tone: 'gold', label: 'Reading partly verified', cls: 'border-warning-500/30 bg-warning-500/10 text-warning-600 dark:text-harvest-300' },
  UNKNOWN: { tone: 'default', label: 'Reading unverified', cls: 'border-line bg-surface-2 text-muted' },
  FAILED: { tone: 'red', label: 'Reading looks wrong', cls: 'border-danger-500/30 bg-danger-500/10 text-danger-600 dark:text-red-300' },
};

/**
 * Skin for a secondary action that sits ON a card, among that card's own text.
 *
 * The kit's `text` variant is right in a toolbar, where everything around it is a control — but on a
 * card whose body is three lines of prose a borderless button IS prose, and "Mark reviewed" / "Set
 * aside…" read as a caption rather than something you can click. So these keep the bordered
 * `default` variant, with the hairline pushed a step darker (`!` because the variant already sets a
 * border colour and this file's `cn` has no tailwind-merge) so the edge survives the card's tint.
 */
const CARD_ACTION = 'border-line-strong! hover:border-brand-500! dark:border-line-strong! dark:hover:text-brand-300';

/** Upper bound on ERP-only cards drawn at once — beyond this the browser, not the server, is the limit. */
const ERP_ONLY_RENDER_CAP = 200;

/**
 * How a reviewer can dispose of an ERP entry the statement never mentioned.
 *
 * Four words that look interchangeable in a dropdown and are not. Three of them RESOLVE the entry —
 * it will clear later, it was checked, it was fixed — and one WRITES IT OFF without resolving
 * anything. An icon says which kind each is at a glance, the one-liner says what it commits you to,
 * and only the write-off is tinted: colouring all four would say they carry equal weight, which is
 * precisely the misreading to avoid.
 */
const ERP_ONLY_META: Record<string, { text: string; icon: JSX.Element; help: string; warn?: boolean }> = {
  PENDING_CLEARANCE: {
    text: 'Pending bank clearance',
    icon: <Clock className="size-4 shrink-0 text-muted" />,
    help: 'It will show on a later statement — a timing difference, not a discrepancy.',
  },
  REVIEWED: {
    text: 'Reviewed — ERP only',
    icon: <Eye className="size-4 shrink-0 text-muted" />,
    help: 'Looked at and accepted as ours alone.',
  },
  CORRECTED: {
    text: 'Corrected',
    icon: <PencilLine className="size-4 shrink-0 text-muted" />,
    help: 'The ERP entry itself was wrong and has been fixed.',
  },
  IGNORED: {
    text: 'Ignore (reason required)',
    icon: <AlertTriangle className="size-4 shrink-0 text-warning-600 dark:text-harvest-300" />,
    help: 'Closes it without resolving it. Your reason is kept on the record.',
    warn: true,
  },
};

const ERP_ONLY_STATES = Object.entries(ERP_ONLY_META).map(([value, m]) => ({
  value,
  title: m.text,
  label: (
    <span className="flex min-w-0 items-start gap-2">
      {m.icon}
      <span className="min-w-0">
        <span className={`block ${m.warn ? 'font-medium text-warning-600 dark:text-harvest-300' : 'text-content'}`}>{m.text}</span>
        <span className="block type-caption text-subtle">{m.help}</span>
      </span>
    </span>
  ),
}));

/** The plain words, for anywhere a chosen verdict is echoed back as text rather than picked. */
const erpOnlyLabel = (state?: string | null) => (state && ERP_ONLY_META[state]?.text) || state || 'Not reviewed';

/** Fields a reconciliation correction may change — every one goes through editPayment. */
const CORRECTABLE = [
  { value: 'retailerId', label: 'Party (retailer)' },
  { value: 'referenceNo', label: 'Reference' },
  { value: 'utr', label: 'UTR' },
  { value: 'mode', label: 'Payment mode' },
  { value: 'paymentDate', label: 'Payment date' },
  { value: 'receivedBankAccountId', label: 'Bank account' },
  { value: 'amount', label: 'Amount' },
];
const MODES = ['CASH', 'CHEQUE', 'UPI', 'NEFT', 'RTGS', 'IMPS', 'CARD', 'OTHER'];

/**
 * Bank statement reconciliation — a side-by-side comparison an accounts user drives by hand.
 *
 * Nothing here writes to a payment. Corrections POST to the reconciliation endpoint, which delegates
 * to `BillingService.editPayment`, so allocation guards, from-credit immutability and the compensating
 * retailer-ledger entries all still apply. Suggested matches are advisory until a human accepts them.
 */
/** Routed page — the reconciliation workspace needs the full width, so it is NOT a modal. */
export function BankReconciliationPage() {
  const { bankAccountId = '' } = useParams();
  return <BankReconciliationWorkspace bankAccountId={bankAccountId} />;
}

export function BankReconciliationWorkspace({ bankAccountId }: { bankAccountId: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  /*
   * WHICH ACCOUNT AM I RECONCILING?
   *
   * The page named the reconciliation, the file and the dates — everything except the one fact that
   * decides whether the work is even valid. The account was only in the URL. Somebody running three
   * accounts could tie a statement out against the wrong one and every figure on the screen would look
   * perfectly consistent while being about a different bank.
   */
  const { data: bankOptions } = useQuery({
    queryKey: ['bank-account-options'],
    queryFn: () => request<any[]>({ url: '/company/bank-accounts/options' }),
  });
  const account = (Array.isArray(bankOptions) ? bankOptions : (bankOptions as any)?.data ?? [])
    .find((b: any) => String(b.id) === String(bankAccountId));
  const accountName = account
    ? `${account.bankName ?? account.label ?? 'Bank account'}${account.last4 ? ` ··${account.last4}` : ''}`
    : null;
  const { token } = theme.useToken();
  const canImport = usePermission('BANK_RECONCILIATION.IMPORT');
  const canMatch = usePermission('BANK_RECONCILIATION.MATCH');
  const canCorrect = usePermission('BANK_RECONCILIATION.CORRECT');
  const canComplete = usePermission('BANK_RECONCILIATION.COMPLETE');
  const canExport = usePermission('BANK_RECONCILIATION.EXPORT');

  const [batchId, setBatchId] = useUrlString<string | undefined>('batch', undefined);
  const [statusF, setStatusF] = useUrlString<string | undefined>('status', undefined);
  const [q, setQ] = useUrlString('q');
  const [amountQ, setAmountQ] = useUrlString('amt');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [candidateRow, setCandidateRow] = useState<any | null>(null);
  const [correctRow, setCorrectRow] = useState<any | null>(null);
  const [ignoreRow, setIgnoreRow] = useState<any | null>(null);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [bulkErpOpen, setBulkErpOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [erpIgnore, setErpIgnore] = useState<any | null>(null);
  // Cross-pane selection: the same statement line is addressable from either side.
  const [selectedRow, setSelectedRow] = useState<string | undefined>();
  const rowRefs = useRef<Record<string, HTMLElement | null>>({});
  const cardRefs = useRef<Record<string, HTMLElement | null>>({});

  // There is only ever ONE reconciliation in play for an account: the open one, or — when none is
  // open — the most recently completed, read-only. No picker, because there is nothing to pick.
  const { data: current } = useQuery({
    queryKey: ['recon-current', bankAccountId],
    queryFn: () => request<any>({ url: '/bank-reconciliations/current', params: { bankAccountId } }),
  });
  const cur = (current as any)?.data ?? current;
  const list = cur ? [cur] : [];
  const activeId = batchId ?? (cur ? String(cur.id) : undefined);

  const { data: cmp, isLoading } = useQuery({
    queryKey: ['recon-comparison', activeId],
    queryFn: () => request<any>({ url: `/bank-reconciliations/${activeId}/comparison` }),
    enabled: !!activeId,
  });

  const act = useMutation({
    mutationFn: ({ url, method = 'POST', data }: { url: string; method?: any; data?: any }) =>
      request({ url: `/bank-reconciliations/${activeId}${url}`, method, data }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['recon-comparison'] }); qc.invalidateQueries({ queryKey: ['recon-batches'] }); },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Action failed'),
  });

  const batch = cmp?.batch;
  const summary = cmp?.summary ?? {};
  const completed = batch?.status === 'COMPLETED';

  const rows = useMemo(() => {
    const all = cmp?.rows ?? [];
    const hay = (v: any) => String(v ?? '').toLowerCase();
    return all.filter((r: any) => {
      if (statusF && r.matchStatus !== statusF) return false;
      if (amountQ && !hay(r.amount).includes(amountQ.trim())) return false;
      if (q) {
        const needle = q.trim().toLowerCase();
        const hit = hay(r.narration).includes(needle) || hay(r.referenceNo).includes(needle)
          || hay(r.erp?.partyName).includes(needle) || hay(r.erp?.docNo).includes(needle);
        if (!hit) return false;
      }
      return true;
    });
  }, [cmp, statusF, q, amountQ]);

  // Statement lines grouped by WHAT THE REVIEWER HAS TO DO about them — not by internal status. A
  // reconciliation is a worklist, so the right-hand pane is ordered by urgency: decisions first,
  // then things missing from one side, then the settled piles.
  const groups = useMemo(() => {
    const by = (...st: string[]) => rows.filter((r: any) => st.includes(r.matchStatus));
    return {
      decide: by('POSSIBLE_MATCH', 'CONFLICT', 'UNMATCHED'),
      bankOnly: by('BANK_ONLY').filter((r: any) => !r.reviewed),
      matched: by('EXACT_MATCH', 'MANUAL_MATCH', 'CORRECTED'),
      setAside: [...by('IGNORED'), ...by('BANK_ONLY').filter((r: any) => r.reviewed)],
    };
  }, [rows]);

  /**
   * Rendered at the SAME position in every branch below. An import is a multi-step conversation with
   * the user (pick a file → confirm the columns → check → import); if a background refetch flips this
   * component between "no reconciliation yet" and the loaded workspace, a per-branch dialog would be
   * unmounted and the user's file and mapping would disappear without a word. One instance, one
   * position, so React never tears it down mid-import.
   */
  const importDialog = (
    <ImportDialog
      open={uploadOpen}
      bankAccountId={bankAccountId}
      onClose={() => setUploadOpen(false)}
      onDone={(id) => {
        setBatchId(String(id));
        qc.invalidateQueries({ queryKey: ['recon-batches'] });
        qc.invalidateQueries({ queryKey: ['recon-current'] });
        qc.invalidateQueries({ queryKey: ['recon-comparison'] });
        qc.invalidateQueries({ queryKey: ['recon-status-by-bank'] });
      }}
    />
  );

  if (!activeId && !list.length) {
    return (
      <>
        <div>
          <Result
            status="info"
            title="No reconciliation yet for this bank account"
            subTitle="Upload the bank's statement to compare it against what the ERP recorded."
            extra={canImport ? <Button variant="primary" icon={<UploadIcon size={16} />} onClick={() => setUploadOpen(true)}>Import statement</Button> : undefined}
          />
        </div>
        {importDialog}
      </>
    );
  }
  if (isLoading || !cmp) {
    return (
      <>
        <div className="flex items-center justify-center gap-2 py-10 text-muted"><Spinner /> Loading…</div>
        {importDialog}
      </>
    );
  }

  const diff = Number(summary.difference ?? 0);

  const total = (ls: any[]) => ls.reduce((a, l) => a + Number(l.amount || 0), 0);
  const creditLines = rows.filter((r: any) => Number(r.creditAmount) > 0).length;
  const erpOnly = (cmp.erpOnly ?? []) as any[];
  const erpPending = erpOnly.filter((e) => !e.handled);

  // Clicking a line on either side scrolls the other pane to the same transaction — going back and
  // forth between the two documents is the entire point of showing both.
  const select = (id: string, from: 'left' | 'right') => {
    setSelectedRow(id);
    const target = from === 'left' ? cardRefs.current[id] : rowRefs.current[id];
    target?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['recon-comparison'] });
    qc.invalidateQueries({ queryKey: ['recon-candidates'] });
  };
  const parse = PARSE_CHECK[batch?.parseCheck ?? 'UNKNOWN'] ?? PARSE_CHECK.UNKNOWN;


  return (
    <>
    <div className="space-y-3">
      {/* No Back button — the app shell already renders one above the page. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm text-muted">
          {/* The account leads. Everything else on this page is only true OF that account. */}
          <b className="text-content">{accountName ?? `Bank account #${bankAccountId}`}</b>
          {' · '}Reconciliation {batch?.reconciliationNo} · {batch?.originalFileName} ·{' '}
          {formatIstDate(batch?.statementFromDate)} → {formatIstDate(batch?.statementToDate)}
          {batch?.openingBalance != null && <> · opening {inr(batch.openingBalance)}</>}
          {batch?.closingBalance != null && <> · closing {inr(batch.closingBalance)}</>}
        </div>
        <Tag tone={completed ? 'green' : batch?.status === 'REVIEWED' ? 'blue' : 'default'} style={{ margin: 0 }}>{batch?.status}</Tag>
        {/*
          * THREE KINDS OF ACTION, ranked, with the blocker underneath rather than wedged inside.
          *
          * The row read Discard · Export · "Outstanding: 6 ERP entries the bank never showed" · Complete
          * — a destructive action, a neutral one, a SENTENCE, and the primary one, all at the same
          * weight and in no particular order. The sentence between two buttons was the worst of it:
          * prose in a button row reads as neither.
          *
          * Now: leaving actions (discard, export) sit quietly on the left of the group, the one action
          * that finishes the work is primary on the right, and the reason it is unavailable is stated
          * on its own line below — where a sentence belongs, and where there is room to say which
          * lines are blocking rather than truncating.
          */}
        <div className="ml-auto flex min-w-0 flex-col items-end gap-1.5">
          {/* Wraps on a phone: Discard · Export · Complete side by side pushed the page 10px sideways
              at 320px, and a header action row is not a data table that may scroll. */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/*
              * RANKED, and grouped by what they do to the work.
              *
              * Three buttons of identical weight said nothing about which one an accountant is meant
              * to reach for. Discard THROWS THE IMPORT AWAY and belongs quiet and set apart; Export
              * takes a copy and changes nothing; Complete is the act the whole screen exists for. The
              * divider is the boundary between "leave this" and "finish this".
              */}
            {canImport && !completed && (
              <>
                {/*
                  * Icon + soft-danger, the treatment this app already gives a destructive TRIGGER
                  * (the approvals hub's "Send back" is the same shape). Plain text with no icon put
                  * the one button that throws away the whole import — and every decision recorded
                  * against it — at less visual weight than Export beside it.
                  *
                  * Soft, not solid red: the confirm dialog behind it is where the full-danger button
                  * lives, and shouting twice trains people to click through both.
                  */}
                <Tooltip label="Throw away this import and every decision recorded against it">
                  <Button size="sm" variant="soft-danger" icon={<Trash2 size={16} />} onClick={() => setDiscardOpen(true)}>
                    Discard
                  </Button>
                </Tooltip>
                <span aria-hidden className="h-5 w-px bg-line" />
              </>
            )}
            {/*
              * ONE EXPORT BUTTON, opening the formats — the same control every other export on this
              * app uses, rather than a bespoke pair. Excel is the primary artifact: the comparison is
              * nineteen columns wide and gets worked in a spreadsheet. No PDF — at that width every
              * cell would clip. CSV stays for anything that ingests the rows downstream.
              */}
            {canExport && (
              <ExportMenu
                baseUrl={`/bank-reconciliations/${activeId}/export`}
                filename={`reconciliation-${batch?.reconciliationNo ?? activeId}`}
                formats={['xlsx', 'csv']}
                label="Export"
                title="Download this comparison"
              />
            )}
            {canImport && completed && (
              /* Nothing can be done on a completed reconciliation except start the next one, so this
                 is the page's primary action and should look like it. */
              <Button size="sm" variant="primary" icon={<UploadIcon size={16} />} onClick={() => setUploadOpen(true)}>Import statement</Button>
            )}
            {canComplete && !completed && (
              <Tooltip label={summary.canComplete
                ? 'Complete this reconciliation'
                : blockerText(summary.blockers) || 'Not everything has been dealt with yet'}>
                <Button
                  variant="primary"
                  size="sm"
                  icon={<Check size={16} />}
                  disabled={!summary.canComplete}
                  onClick={() => setCompleteOpen(true)}
                >
                  Complete
                </Button>
              </Tooltip>
            )}
          </div>
          {/* Why Complete is greyed out, in full, on its own line. */}
          {canComplete && !completed && !summary.canComplete && blockerText(summary.blockers) && (
            <span className="flex items-center gap-1.5 text-xs text-warning-600 dark:text-harvest-300">
              <AlertTriangle size={13} className="shrink-0" />
              {blockerText(summary.blockers)}
            </span>
          )}
        </div>
      </div>

      {/* How far the file could be trusted. Shown ALWAYS, not only on failure — a reconciliation built
          on a reading nobody vouched for is worse than none, because it looks authoritative. */}
      <div className={`flex items-start gap-2 rounded-control border px-3 py-2 text-xs ${parse.cls}`}>
        {batch?.parseCheck === 'OK' ? <Check size={14} className="mt-0.5 shrink-0" /> : <AlertTriangle size={14} className="mt-0.5 shrink-0" />}
        <span><b>{parse.label}.</b> {batch?.parseNote}</span>
      </div>

      {summary.erpTruncated && (
        <div className="flex items-start gap-2 rounded-control border border-danger-500/30 bg-danger-500/10 px-3 py-2 text-sm text-danger-600 dark:text-red-300">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            <b>This comparison is incomplete.</b> The account has more than {Number(summary.erpWindowCap).toLocaleString('en-IN')} entries
            in this period, so the ERP side was cut short and some lines may be shown as "Not in the ERP"
            when a match exists. Import a shorter period.
          </div>
        </div>
      )}

      {/* Five figures, in the order a reviewer asks them: what came in, what agrees, what needs me,
          and what is missing from each side. */}
      <Row gutter={[8, 8]}>
        <Col xs={12} md={8} lg={4}><StatCard label="Credits in bank" value={inr(summary.bankCredits)} hint={`${creditLines} received`} tone="sky" /></Col>
        <Col xs={12} md={8} lg={5}><StatCard label="Matched" value={inr(total(groups.matched))} hint={`${groups.matched.length} ${groups.matched.length === 1 ? 'transaction' : 'transactions'}`} tone="success" /></Col>
        <Col xs={12} md={8} lg={5}><StatCard label="Needs a decision" value={inr(total(groups.decide))} hint={`${groups.decide.length} ${groups.decide.length === 1 ? 'transaction' : 'transactions'}`} tone="gold" /></Col>
        <Col xs={12} md={12} lg={5}><StatCard label="Not in the ERP" value={inr(total(groups.bankOnly))} hint={`${groups.bankOnly.length} ${groups.bankOnly.length === 1 ? 'transaction' : 'transactions'}`} tone="brand" /></Col>
        <Col xs={12} md={12} lg={5}><StatCard label="Not in the bank" value={inr(erpPending.reduce((a: number, e: any) => a + Number(e.amount || 0), 0))} hint={`${erpPending.length} entries`} tone="gold" /></Col>
      </Row>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        {/* ── LEFT: the statement exactly as it was read, so it can be followed against the paper ── */}
        <Card className="min-h-0">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Landmark size={16} className="text-muted" />
            <span className="font-semibold">Bank statement</span>
            <span className="text-xs text-muted">{rows.length} rows as read from the file</span>
            <div className="ml-auto flex flex-wrap items-center gap-1.5">
              <Select placeholder="Status" allowClear size="sm" style={{ width: 150 }} value={statusF} onChange={(v) => setStatusF(v as string | undefined)}
                options={Object.entries(STATUS).map(([value, st]) => ({ value, label: st.label }))} />
              <Input allowClear size="sm" placeholder="Narration / ref / party" style={{ width: 190 }} value={q} onChange={(e: any) => setQ(e.target.value)} />
              <Input allowClear size="sm" placeholder="Amount" style={{ width: 100 }} value={amountQ} onChange={(e: any) => setAmountQ(e.target.value)} />
            </div>
          </div>
          {/* Grows with the content but never taller than the pane beside it, so the two stay level
              and neither drags the modal into a long outer scroll. */}
          <div className="max-h-[58vh] overflow-auto rounded-control border border-line">
            <table className="w-full text-xs">
              <thead className="gb-thead-tint sticky top-0 bg-surface-2 text-subtle">
                <tr>
                  <th className="px-2 py-1.5 text-left font-semibold">Date</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Narration</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Withdrawn</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Deposited</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Balance</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Verdict</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((l: any) => (
                  <tr
                    key={l.id}
                    ref={(el) => { rowRefs.current[String(l.id)] = el; }}
                    onClick={() => select(String(l.id), 'left')}
                    className={`cursor-pointer border-t border-line ${String(l.id) === selectedRow ? 'bg-brand-500/10' : 'hover:bg-surface-2'}`}
                  >
                    <td className="whitespace-nowrap px-2 py-1.5">{formatIstDate(l.transactionDate)}</td>
                    <td className="max-w-[240px] px-2 py-1.5">
                      <div className="truncate" title={l.narration ?? ''}>{l.narration || <span className="text-subtle">—</span>}</div>
                      {l.referenceNo && <div className="truncate text-2xs text-subtle" title={l.referenceNo}>{l.referenceNo}</div>}
                    </td>
                    <td className="px-2 py-1.5 text-right text-warning-600 dark:text-harvest-300">{Number(l.debitAmount) > 0 ? inr(l.debitAmount) : ''}</td>
                    <td className="px-2 py-1.5 text-right font-medium text-brand-700 dark:text-brand-300">{Number(l.creditAmount) > 0 ? inr(l.creditAmount) : ''}</td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-right text-muted">{l.runningBalance ? inr(l.runningBalance) : ''}</td>
                    <td className="px-2 py-1.5"><Tag tone={STATUS[l.matchStatus]?.tone ?? 'default'} style={{ margin: 0 }}>{STATUS[l.matchStatus]?.label ?? l.matchStatus}</Tag></td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={6} className="px-2 py-6 text-center text-muted">No statement rows match these filters</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>

        {/* ── RIGHT: what we concluded, grouped by what has to be DONE about it ── */}
        <div className="flex max-h-[66vh] min-h-0 flex-col gap-3 overflow-auto pr-1">
          <div className="flex items-center gap-1.5 px-1 type-body-strong text-content">
            <ArrowRight size={14} className="text-muted" /> Compared with the ERP
          </div>
          <Section
            title="Needs a decision" tone="text-warning-600 dark:text-harvest-300" lines={groups.decide}
            empty="Nothing waiting on you."
            {...{ cardRefs, selectedRow, select, act, canMatch, canCorrect, completed, setCorrectRow, setIgnoreRow, batchId: activeId, refresh }}
          />
          <Section
            title="In the bank, not in the ERP" tone="text-sky-600" lines={groups.bankOnly}
            empty="Every statement transaction has an ERP counterpart or has been dealt with."
            hint="Usually a bank charge or interest — this ERP does not record those. Mark reviewed with a note."
            {...{ cardRefs, selectedRow, select, act, canMatch, canCorrect, completed, setCorrectRow, setIgnoreRow, batchId: activeId, refresh }}
          />

          {/* Not in the bank — ERP entries the statement never mentioned. */}
          <Card>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="font-semibold text-warning-600 dark:text-harvest-300">In the ERP, not in the bank</span>
              <span className="text-xs text-muted">{summary.erpOnlyHandled ?? 0} of {summary.erpOnlyCount ?? 0} handled</span>
              {canMatch && !completed && erpPending.length > 0 && (
                <Button className="ml-auto" size="sm" onClick={() => setBulkErpOpen(true)}>Mark all remaining…</Button>
              )}
            </div>
            {!erpOnly.length ? (
              <div className="py-3 text-sm text-muted">Nothing — the statement accounted for every ERP entry.</div>
            ) : (
              <div className="space-y-1">
                {erpOnly.slice(0, ERP_ONLY_RENDER_CAP).map((e) => (
                  // Wraps: beside the 190px review picker a 320px phone left the entry 80px, so
                  // "07 Sep 2026 · SLIP · money in ₹10,000" lost its amount. The picker drops under it instead.
                  <div key={`${e.sourceType}-${e.sourceId}`} className="flex flex-wrap items-center gap-2 rounded-control border border-line p-2 text-xs">
                    <div className="min-w-0 flex-[1_1_12rem]">
                      <div className="truncate font-medium text-content">
                        {e.docNo} · {e.kind === 'COLLECTION'
                          ? <RetailerLink id={e.retailerId}>{e.partyName}</RetailerLink>
                          : <UserLink id={e.partyUserId} role={e.partyRole}>{e.partyName}</UserLink>}
                      </div>
                      <div className="truncate text-subtle">
                        {formatIstDate(e.date)} · {e.mode ?? '—'} · {e.direction === 'IN' ? 'money in' : 'money out'} {inr(e.amount)}
                        {e.orderNo ? <> · <OrderLink id={e.orderId}>{e.orderNo}</OrderLink></> : null}
                      </div>
                    </div>
                    {canMatch && !completed ? (
                      <Select
                        style={{ width: 190 }} size="sm" placeholder="Not reviewed — pick one"
                        value={e.review?.state}
                        onChange={(v: any) => {
                          if (!v) return;
                          if (v === 'IGNORED') { setErpIgnore(e); return; }
                          confirmAction(
                            { title: `Mark this entry as ${String(v).toLowerCase()}?`, content: 'It is recorded against your name on the reconciliation.', okText: 'Yes, mark it' },
                            () => act.mutate({ url: '/erp-only/review', data: { sourceType: e.sourceType, sourceId: String(e.sourceId), state: v } }),
                          );
                        }}
                        options={ERP_ONLY_STATES}
                      />
                    ) : (
                      <Tag tone={e.review ? 'green' : 'gold'} style={{ margin: 0 }}>
                        {erpOnlyLabel(e.review?.state)}
                      </Tag>
                    )}
                  </div>
                ))}
                {erpOnly.length > ERP_ONLY_RENDER_CAP && (
                  // Rendering thousands of cards would lock the tab. The count is stated rather than
                  // hidden, and "Mark all remaining" still applies to EVERY unhandled entry, not just
                  // the ones on screen.
                  <div className="rounded-control bg-surface-2 px-2 py-1.5 text-xs text-muted">
                    Showing the first {ERP_ONLY_RENDER_CAP} of {erpOnly.length}. Use the filters, or
                    “Mark all remaining” — it applies to all {erpPending.length} unhandled entries.
                  </div>
                )}
              </div>
            )}
          </Card>

          <Section
            title="Matched" tone="text-brand-700 dark:text-brand-300" lines={groups.matched} collapsed
            empty="Nothing matched yet."
            {...{ cardRefs, selectedRow, select, act, canMatch, canCorrect, completed, setCorrectRow, setIgnoreRow, batchId: activeId, refresh }}
          />
          <Section
            title="Set aside" tone="text-muted" lines={groups.setAside} collapsed
            empty="Nothing set aside."
            {...{ cardRefs, selectedRow, select, act, canMatch, canCorrect, completed, setCorrectRow, setIgnoreRow, batchId: activeId, refresh }}
          />
        </div>
      </div>


      <CandidateDrawer batchId={activeId!} row={candidateRow} onClose={() => setCandidateRow(null)}
        onMatched={() => { setCandidateRow(null); qc.invalidateQueries({ queryKey: ['recon-comparison'] }); }} />

      <CorrectionDialog batchId={activeId!} row={correctRow} onClose={() => setCorrectRow(null)}
        onSaved={() => { setCorrectRow(null); qc.invalidateQueries({ queryKey: ['recon-comparison'] }); }} />

      <IgnoreDialog row={ignoreRow} onClose={() => setIgnoreRow(null)}
        onConfirm={(reason) => { act.mutate({ url: `/rows/${ignoreRow.id}/ignore`, data: { reason } }); setIgnoreRow(null); }} />

      <BulkErpDialog
        open={bulkErpOpen} pending={(cmp.erpOnly ?? []).filter((e: any) => !e.handled)}
        onClose={() => setBulkErpOpen(false)}
        onConfirm={(state, note) => {
          act.mutate({
            url: '/erp-only/review-bulk',
            data: { entries: (cmp.erpOnly ?? []).filter((e: any) => !e.handled).map((e: any) => ({ sourceType: e.sourceType, sourceId: String(e.sourceId) })), state, note },
          }, { onSuccess: () => setBulkErpOpen(false) });
        }} />

      <IgnoreDialog row={erpIgnore} onClose={() => setErpIgnore(null)}
        onConfirm={(reason) => {
          act.mutate({ url: '/erp-only/review', data: { sourceType: erpIgnore.sourceType, sourceId: String(erpIgnore.sourceId), state: 'IGNORED', note: reason } });
          setErpIgnore(null);
        }} />

      <Modal
        open={discardOpen}
        onOpenChange={(o: boolean) => { if (!o) setDiscardOpen(false); }}
        title={`Discard ${batch?.reconciliationNo}?`}
        footer={<>
          <Button variant="default" onClick={() => setDiscardOpen(false)}>Keep it</Button>
          <Button variant="danger" onClick={() => {
            request({ url: `/bank-reconciliations/${activeId}`, method: 'DELETE' })
              .then(() => {
                message.success('Reconciliation discarded');
                setDiscardOpen(false); setBatchId(undefined);
                qc.invalidateQueries({ queryKey: ['recon-current'] });
                qc.invalidateQueries({ queryKey: ['recon-status-by-bank'] });
              })
              .catch((e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not discard'));
          }}>Discard</Button>
        </>}>
        <div className="space-y-2 text-sm">
          <div>
            This throws away the imported statement and every decision recorded against it. The account is
            then free for another import.
          </div>
          <div className="text-muted">
            Nothing financial is affected — no payment or ledger entry was ever changed by this
            reconciliation, and the ERP entries it was holding are released.
          </div>
        </div>
      </Modal>

      <CompleteDialog open={completeOpen} summary={summary} onClose={() => setCompleteOpen(false)}
        onConfirm={(note) => {
          act.mutate({ url: '/complete', data: { note } }, {
            onSuccess: () => { message.success('Reconciliation completed'); setCompleteOpen(false); },
          });
        }} />
    </div>
    {importDialog}
    </>
  );
}

/** Expanded row: the statement line on the left, the ERP entry on the right, field for field. */
function RowDetail({ row }: { row: any }) {
  const F = ({ label, value }: { label: string; value: any }) => (
    <div className="flex justify-between gap-3 py-1">
      <span className="text-xs uppercase tracking-wide text-subtle">{label}</span>
      <span className="text-sm text-content">{value ?? <span className="text-subtle">—</span>}</span>
    </div>
  );
  const e = row.erp;
  return (
    // The comparison table is wider than the viewport, so an expanded row would inherit that width and
    // clip its right-hand panel. Pinning it to the left edge of the scroll container and capping it at
    // the viewport keeps both panels fully readable at any horizontal scroll position.
    <div className="sticky left-0 w-[min(1100px,calc(100vw-7rem))] grid gap-4 p-3 md:grid-cols-2">
      <div className="rounded-control border border-default p-3">
        <div className="mb-2 font-semibold">Bank statement</div>
        <F label="Transaction date" value={formatIstDate(row.transactionDate)} />
        <F label="Value date" value={row.valueDate ? formatIstDate(row.valueDate) : null} />
        <F label="Narration" value={row.narration} />
        <F label="Reference" value={row.referenceNo} />
        <F label="Cheque no" value={row.chequeNo} />
        <F label="Debit" value={Number(row.debitAmount) > 0 ? inr(row.debitAmount) : null} />
        <F label="Credit" value={Number(row.creditAmount) > 0 ? inr(row.creditAmount) : null} />
        <F label="Running balance" value={row.runningBalance ? inr(row.runningBalance) : null} />
        <F label="Statement row" value={`#${row.originalRowNumber}`} />
      </div>
      <div className="rounded-control border border-default p-3">
        <div className="mb-2 font-semibold">ERP entry</div>
        {!e ? (
          <div className="py-4 text-sm text-muted">
            No ERP entry is matched to this line. It may be a bank charge or interest — which this ERP does not
            record — or a collection that was never entered. Mark it reviewed with a note, or match it manually.
          </div>
        ) : (
          <>
            <F label="Document" value={e.docNo} />
            <F label="Date" value={formatIstDate(e.date)} />
            <F label="Type" value={e.kind === 'PAYOUT' ? (e.payoutType ?? 'Payout') : 'Collection'} />
            <F label="Party" value={e.partyName} />
            <F label="Linked order" value={e.orderNo} />
            <F label="Mode" value={e.mode} />
            <F label="Reference" value={e.reference} />
            <F label="Amount" value={inr(e.amount)} />
            <F label="Direction" value={e.direction === 'IN' ? 'Money in' : 'Money out'} />
            <F label="Recorded by" value={e.actorName} />
          </>
        )}
        {row.matchReason && <F label="Why it was suggested" value={row.matchReason} />}
        {row.reviewNote && <F label="Note" value={row.reviewNote} />}
        {row.ignoredReason && <F label="Ignored because" value={row.ignoredReason} />}
      </div>
    </div>
  );
}

/** Upload → map columns → dry-run → import. Parsing is entirely server-side. */
export function ImportDialog({ open, bankAccountId, onClose, onDone }: { open: boolean; bankAccountId: string; onClose: () => void; onDone: (batchId: string) => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [step, setStep] = useState<'pick' | 'map'>('pick');
  const [headers, setHeaders] = useState<string[]>([]);
  const [map, setMap] = useState<any>({ headerRow: 1 });
  const [dry, setDry] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [signed, setSigned] = useState(false);
  const [mapNote, setMapNote] = useState<string | null>(null);
  /* What the PDF reader had to infer rather than compute. A PDF has no columns, so on some lines
     money-in vs money-out is read from the narration — the reviewer has to be told which. */
  const [pdfNote, setPdfNote] = useState<string | null>(null);
  /* Why the file was refused. A toast is the wrong home for this: the message names the exact lines
     that could not be read, and it disappeared while the reader was still finding them. */
  const [readError, setReadError] = useState<string | null>(null);
  const [showCols, setShowCols] = useState(false);

  const reset = () => { setFile(null); setStep('pick'); setHeaders([]); setMap({ headerRow: 1 }); setDry(null); setSigned(false); setMapNote(null); setShowCols(false); };
  const post = async (path: string, fd: FormData) => {
    const res = await request<any>({ url: `/bank-reconciliations/${path}`, method: 'POST', data: fd, headers: { 'Content-Type': 'multipart/form-data' } });
    return (res as any)?.data ?? res;
  };

  const onPick = async (f: File) => {
    setFile(f); setBusy(true); setReadError(null);
    try {
      const fd = new FormData(); fd.append('file', f); fd.append('bankAccountId', bankAccountId);
      const r = await post('preview', fd);
      setHeaders(r.headers ?? []);
      setMapNote(r.savedMappingRejected ?? null);
      setPdfNote(r.pdfNote ?? null);
      // A saved mapping for this bank is applied automatically, so the second statement is one click.
      // Saved mapping for this bank wins; otherwise the server's header-alias guess, so even the FIRST
      // statement from a bank arrives pre-filled rather than eight empty pickers.
      setMap(r.savedMapping ?? r.suggestedMapping ?? { headerRow: r.headerRowGuess ?? 1 });
      const m = r.savedMapping ?? r.suggestedMapping ?? { headerRow: r.headerRowGuess ?? 1 };
      setSigned(!!m.signedAmount);
      setStep('map');
      // Read the file with that mapping immediately. Showing the actual transactions answers the only
      // question the user has — "did it understand my statement?" — far better than eight dropdowns do.
      const complete = !!m.transactionDate && (m.signedAmount || m.debitAmount || m.creditAmount);
      setShowCols(!complete);
      if (complete) {
        try {
          const fd2 = new FormData(); fd2.append('file', f); fd2.append('mapping', JSON.stringify(m));
          setDry(await post('preview-mapping', fd2));
        } catch { setShowCols(true); }
      }
    } catch (e: any) {
      /* Kept on screen, not flashed. The refusal names which transaction lines could not be read — the
         reader has to be able to go and look at them in the statement while it is still in front of
         them, and then choose the CSV instead. */
      setReadError(e?.response?.data?.error?.message ?? 'Could not read that file.');
      setFile(null);
    } finally { setBusy(false); }
  };

  const runDry = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('mapping', JSON.stringify(map));
      setDry(await post('preview-mapping', fd));
    } catch (e: any) {
      message.error(e?.response?.data?.error?.message ?? 'Mapping failed');
    } finally { setBusy(false); }
  };

  const doImport = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('bankAccountId', bankAccountId); fd.append('mapping', JSON.stringify(map));
      const r = await post('import', fd);
      message.success(`Imported ${r.imported} rows${r.skipped ? ` (${r.skipped} skipped)` : ''}`);
      onDone(String(r.batchId)); reset(); onClose();
    } catch (e: any) {
      message.error(e?.response?.data?.error?.message ?? 'Import failed');
    } finally { setBusy(false); }
  };

  const opts = headers.filter(Boolean).map((h) => ({ value: h, label: h }));
  const set = (k: string) => (v: any) => setMap((m: any) => ({ ...m, [k]: v ?? null }));

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }} title="Import bank statement" width={760}
      footer={
        <>
          <Button variant="default" onClick={() => { reset(); onClose(); }}>Cancel</Button>
          {step === 'map' && showCols && <Button onClick={runDry} loading={busy}>Re-read the file</Button>}
          {step === 'map' && <Button variant="primary" loading={busy} disabled={!map.transactionDate || (!signed ? !(map.debitAmount || map.creditAmount) : !map.signedAmount)} onClick={doImport}>Import</Button>}
        </>
      }>
      {step === 'pick' ? (
        <div className="space-y-3">
          <Upload.Dragger
            accept=".csv,.xls,.xlsx,.pdf"
            multiple={false}
            maxCount={1}
            beforeUpload={(f: File) => { onPick(f); return false; }}
          >
            <div className="py-2">
              <UploadIcon size={24} className="mx-auto opacity-60" />
              <div className="mt-2 font-medium text-content">Click or drop your bank statement here</div>
              <div className="mt-0.5 text-xs text-muted">CSV, Excel or PDF · up to 10 MB</div>
            </div>
          </Upload.Dragger>
          {busy ? (
            <div className="flex items-center justify-center gap-2 py-1 text-sm text-muted"><Spinner /> Reading the file…</div>
          ) : readError ? (
            <Alert
              type="error"
              message="That file was not imported"
              description={readError}
              icon={<AlertTriangle size={16} />}
            />
          ) : (
            <div className="rounded-control bg-surface-2 px-3 py-2 text-xs text-muted">
              Nothing is saved yet. You will confirm which column is which, see exactly what will be
              imported, and only then create the reconciliation.
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 rounded-control bg-surface-2 px-3 py-2">
            <FileSpreadsheet size={16} className="text-brand-600" />
            <span className="type-body-medium text-content">{file?.name}</span>
            <Button className="ml-auto" variant="text" size="sm" onClick={() => { setStep('pick'); setDry(null); }}>Choose a different file</Button>
          </div>
          {/* What we understood, in words. The column pickers are an escape hatch below, not the
              main event — most statements are read correctly and the user should only have to look
              at the transactions and press Import. */}
          <ReadSummary map={map} signed={signed} dry={dry} note={mapNote} />
          {pdfNote && (
            <Alert
              type="warning"
              className="mt-2"
              message="Some of this PDF was read by inference, not arithmetic"
              description={pdfNote}
              icon={<AlertTriangle size={16} />}
            />
          )}

          {dry?.sample?.length > 0 && (
            <div>
              <div className="mb-1 type-overline text-subtle">
                The first {Math.min(5, dry.sample.length)} transactions, as we read them
              </div>
              <div className="overflow-hidden rounded-control border border-line">
                <table className="w-full text-xs">
                  <thead className="gb-thead-tint bg-surface-2 text-subtle">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-semibold">Date</th>
                      <th className="px-2 py-1.5 text-left font-semibold">Narration</th>
                      <th className="px-2 py-1.5 text-right font-semibold">Money out</th>
                      <th className="px-2 py-1.5 text-right font-semibold">Money in</th>
                      <th className="px-2 py-1.5 text-right font-semibold">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dry.sample.slice(0, 5).map((r: any, i: number) => (
                      <tr key={i} className="border-t border-line">
                        <td className="whitespace-nowrap px-2 py-1.5">{formatIstDate(r.transactionDate)}</td>
                        <td className="max-w-[260px] truncate px-2 py-1.5" title={r.narration ?? ''}>{r.narration || <span className="text-subtle">—</span>}</td>
                        <td className="px-2 py-1.5 text-right text-warning-600 dark:text-harvest-300">{Number(r.debitAmount) > 0 ? inr(r.debitAmount) : ''}</td>
                        <td className="px-2 py-1.5 text-right font-medium text-brand-700 dark:text-brand-300">{Number(r.creditAmount) > 0 ? inr(r.creditAmount) : ''}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-right text-muted">{r.runningBalance ? inr(r.runningBalance) : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <button
            type="button"
            className="flex items-center gap-1.5 type-label text-muted hover:text-content"
            onClick={() => setShowCols((v) => !v)}
          >
            {showCols ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            {showCols ? 'Hide column settings' : "Doesn't look right? Change which column is which"}
          </button>

          {showCols && (
          <div className="space-y-4 rounded-control border border-line bg-surface-2/50 p-3">
            <div className="text-xs text-muted">
              Point each row at the matching column in your file, then press <b>Re-read the file</b> to see
              the result. What you choose is remembered for this bank account.
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Header row"><Input type="number" value={String(map.headerRow ?? 1)} onChange={(e: any) => set('headerRow')(Number(e.target.value) || 1)} /></Field>
              <Field label="Footer rows to ignore"><Input type="number" value={String(map.skipFooterRows ?? 0)} onChange={(e: any) => set('skipFooterRows')(Number(e.target.value) || 0)} /></Field>
              <Field label="Transaction date *"><Select allowClear value={map.transactionDate} onChange={set('transactionDate')} options={opts} className="w-full" /></Field>
              <Field label="Value date"><Select allowClear value={map.valueDate} onChange={set('valueDate')} options={opts} className="w-full" /></Field>
              <Field label="Narration"><Select allowClear value={map.narration} onChange={set('narration')} options={opts} className="w-full" /></Field>
              <Field label="Reference / UTR"><Select allowClear value={map.referenceNo} onChange={set('referenceNo')} options={opts} className="w-full" /></Field>
              <Field label="Cheque number"><Select allowClear value={map.chequeNo} onChange={set('chequeNo')} options={opts} className="w-full" /></Field>
              <Field label="Running balance"><Select allowClear value={map.runningBalance} onChange={set('runningBalance')} options={opts} className="w-full" /></Field>
            </div>
            <div className="border-t border-line pt-3 type-overline text-subtle">Amount columns</div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={signed} onChange={(e) => { setSigned(e.target.checked); setMap((m: any) => ({ ...m, debitAmount: null, creditAmount: null, signedAmount: null })); }} />
              This statement uses one signed Amount column instead of separate Debit and Credit columns
            </label>
            {signed ? (
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Amount column *"><Select allowClear value={map.signedAmount} onChange={set('signedAmount')} options={opts} className="w-full" /></Field>
                <Field label="A positive number means">
                  <Select value={map.signedPositiveIsCredit === false ? 'debit' : 'credit'} className="w-full"
                    onChange={(v: any) => set('signedPositiveIsCredit')(v === 'credit')}
                    options={[{ value: 'credit', label: 'Money in (credit)' }, { value: 'debit', label: 'Money out (debit)' }]} />
                </Field>
              </div>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Debit (withdrawal) *"><Select allowClear value={map.debitAmount} onChange={set('debitAmount')} options={opts} className="w-full" /></Field>
                <Field label="Credit (deposit) *"><Select allowClear value={map.creditAmount} onChange={set('creditAmount')} options={opts} className="w-full" /></Field>
              </div>
            )}
          </div>
          )}

          {/* Only the things that need attention. The headline figures are already stated above — repeating
              them here made the screen look like a form to fill in rather than a reading to confirm. */}
          {dry && (dry.duplicateRows > 0 || dry.dateOrderReversed || dry.errorCount > 0) && (
            <div className="space-y-1.5 text-sm">
              {dry.duplicateRows > 0 && (
                <div className="rounded-control bg-surface-2 px-3 py-1.5 text-xs text-muted">
                  {dry.duplicateRows} transaction{dry.duplicateRows === 1 ? '' : 's'} in this file {dry.duplicateRows === 1 ? 'is' : 'are'} identical to another. They will all be
                  imported — banks do print genuine repeats.
                </div>
              )}
              {dry.dateOrderReversed && (
                <div className="rounded-control bg-surface-2 px-3 py-1.5 text-xs text-muted">
                  This statement is printed newest-first. It will be stored oldest-first so it reads in order.
                </div>
              )}
              {dry.errorCount > 0 && (
                <div className="mt-2 rounded-control bg-warning-500/10 p-2 text-warning-600 dark:text-harvest-300">
                  <div className="font-medium">{dry.errorCount} row(s) will be skipped:</div>
                  <ul className="ml-4 list-disc">{dry.errors.slice(0, 5).map((e: any, i: number) => <li key={i}>Row {e.row}: {e.message}</li>)}</ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

/**
 * How the file was understood, said in words. The point is that a person can confirm or reject the
 * reading without knowing what a "column mapping" is.
 */
function ReadSummary({ map, signed, dry, note }: { map: any; signed: boolean; dry: any; note: string | null }) {
  const missing: string[] = [];
  if (!map.transactionDate) missing.push('the transaction date');
  if (signed ? !map.signedAmount : !(map.debitAmount || map.creditAmount)) missing.push('the amount');

  if (missing.length) {
    return (
      <div className="rounded-control border border-warning-500/30 bg-warning-500/10 px-3 py-2 text-sm text-warning-600 dark:text-harvest-300">
        <b>We could not work out {missing.join(' or ')} in this file.</b> Open the column settings below
        and point us at the right columns — nothing is imported until you do.
      </div>
    );
  }

  const parse = PARSE_CHECK[dry?.parseCheck ?? 'UNKNOWN'] ?? PARSE_CHECK.UNKNOWN;
  return (
    <div className="space-y-2">
      <div className={`flex items-start gap-2 rounded-control border px-3 py-2 text-sm ${dry ? parse.cls : 'border-line bg-surface-2 text-muted'}`}>
        {dry?.parseCheck === 'OK' ? <Check size={16} className="mt-0.5 shrink-0" /> : <AlertTriangle size={16} className="mt-0.5 shrink-0" />}
        <div>
          {dry ? (
            <>
              <b>
                {dry.total} transaction{dry.total === 1 ? '' : 's'} read
                {dry.from ? <> · {formatIstDate(dry.from)} to {formatIstDate(dry.to)}</> : null}
              </b>
              <div className="mt-0.5">
                {inr(dry.totalCredit)} in · {inr(dry.totalDebit)} out. {dry.parseNote}
              </div>
            </>
          ) : (
            <b>Reading the file…</b>
          )}
        </div>
      </div>
      {note && (
        <div className="rounded-control bg-surface-2 px-3 py-1.5 text-xs text-muted">{note}</div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return <div><div className="mb-1 text-xs text-muted">{label}</div>{children}</div>;
}

/** Pick a different ERP entry for a statement row. Suggestions first, then everything on the account. */
function CandidateDrawer({ batchId, row, onClose, onMatched }: { batchId: string; row: any | null; onClose: () => void; onMatched: () => void }) {
  const { data } = useQuery({
    queryKey: ['recon-candidates', batchId, row?.id],
    queryFn: () => request<any>({ url: `/bank-reconciliations/${batchId}/rows/${row.id}/candidates` }),
    enabled: !!row,
  });
  const pick = useConfirmedMutation({ title: "Match this statement transaction to the selected entry?", content: "Both are marked reconciled against each other.", okText: "Yes, match" }, {
    mutationFn: (e: any) => request({ url: `/bank-reconciliations/${batchId}/rows/${row.id}/match`, method: 'POST', data: { sourceType: e.sourceType, sourceId: String(e.sourceId) } }),
    onSuccess: () => { message.success('Matched'); onMatched(); },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not match'),
  });
  const Item = ({ e, suggested }: { e: any; suggested?: boolean }) => (
    <button type="button" onClick={() => pick.mutate(e)}
      className="flex w-full items-center justify-between gap-3 rounded-control border border-default p-2 text-left hover:bg-brand-500/5">
      <div className="min-w-0">
        <div className="truncate type-body-medium">{e.docNo} · {e.partyName ?? '—'}</div>
        <div className="truncate text-xs text-muted">
          {formatIstDate(e.date)} · {e.mode ?? '—'} · {e.reference ?? 'no reference'}
        </div>
        {e.reason && <div className="truncate text-2xs text-subtle" title={e.reason}>{e.reason}</div>}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {suggested && typeof e.score === 'number' && (
          <Tag tone={e.score >= 70 ? 'green' : 'gold'} style={{ margin: 0 }}>{e.score}%</Tag>
        )}
        <span className="whitespace-nowrap font-medium">{inr(e.amount)}</span>
      </div>
    </button>
  );
  return (
    <Drawer open={!!row} onClose={onClose} title="Choose the ERP entry" width={520}>
      {row && (
        <div className="space-y-3">
          <div className="rounded-control bg-surface-2 p-2 text-sm">
            <b>{formatIstDate(row.transactionDate)}</b> · {row.narration || 'no narration'} · <b>{inr(row.amount)}</b> {row.direction === 'IN' ? 'in' : 'out'}
          </div>
          <div>
            <div className="mb-1 text-xs uppercase tracking-wide text-subtle">Suggested — same amount and direction</div>
            <div className="space-y-1">
              {(data?.suggested ?? []).length === 0 && <div className="text-sm text-muted">Nothing on this account matches that amount and direction.</div>}
              {(data?.suggested ?? []).map((e: any) => <Item key={`${e.sourceType}-${e.sourceId}`} e={e} suggested />)}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs uppercase tracking-wide text-subtle">Anything else on this bank account</div>
            <div className="max-h-[45vh] space-y-1 overflow-y-auto">
              {(data?.others ?? []).map((e: any) => <Item key={`${e.sourceType}-${e.sourceId}`} e={e} />)}
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
}

/**
 * Correct the matched ERP payment. Every field here is applied by the existing editPayment service.
 *
 * A centred dialog, NOT a drawer: this workspace is itself a modal, and a panel sliding in from the
 * right covered the ERP column — the very entry being corrected — while leaving the statement it is
 * being corrected against half-visible behind a second overlay. The other decisions taken here (set
 * aside, complete, bulk-mark) are all dialogs; a one-field form is no different.
 */
function CorrectionDialog({ batchId, row, onClose, onSaved }: { batchId: string; row: any | null; onClose: () => void; onSaved: () => void }) {
  const [field, setField] = useState<string | undefined>();
  const [value, setValue] = useState<any>('');
  const [reason, setReason] = useState('');

  const { data: pv } = useQuery({
    queryKey: ['recon-correct-preview', batchId, row?.id],
    queryFn: () => request<any>({ url: `/bank-reconciliations/${batchId}/rows/${row.id}/correction-preview` }),
    enabled: !!row,
  });
  // Searched on the server; the picked retailer stays listed when a later search does not return it.
  const retailerPick = useRetailerSearch({
    key: 'recon-retailers', enabled: !!row && field === 'retailerId', selected: field === 'retailerId' ? value : undefined,
  });
  const { data: banks } = useQuery({
    queryKey: ['bank-account-options'], queryFn: () => request<any[]>({ url: '/company/bank-accounts/options' }),
    enabled: !!row && field === 'receivedBankAccountId',
  });

  const save = useConfirmedMutation({ title: "Save this correction?", content: "The corrected value and your reason are recorded against the statement transaction.", okText: "Yes, save" }, {
    mutationFn: () => request({ url: `/bank-reconciliations/${batchId}/rows/${row.id}/correct`, method: 'POST', data: { field, value, reason } }),
    onSuccess: () => { message.success('Correction applied'); setField(undefined); setValue(''); setReason(''); onSaved(); },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Correction failed'),
  });

  const p = pv?.payment;
  const blocks = pv?.blocks ?? {};
  const partyBlocked = field === 'retailerId' && blocks.allocatedBlocksParty;
  const current = p ? ({
    retailerId: p.retailerName, referenceNo: p.referenceNo, utr: p.utr, mode: p.mode,
    paymentDate: p.paymentDate, receivedBankAccountId: p.receivedBankAccountId, amount: p.amount,
} as any)[field ?? ''] : null;

  const cashPayment = String(p?.mode ?? '').toUpperCase() === 'CASH';
  const close = () => { setField(undefined); setValue(''); setReason(''); onClose(); };
  const blocked = !!partyBlocked || !!(pv && !pv.correctable);

  return (
    <Modal
      open={!!row}
      onOpenChange={(o) => { if (!o) close(); }}
      width={560}
      title="Correct the ERP payment"
      // The standing rule about this form, so it is read before the field is picked rather than
      // found as a footnote under the Save button.
      description="Direction and transaction type cannot be changed — reverse the entry and record the correct one instead."
      footer={
        <>
          <Button variant="default" onClick={close}>Cancel</Button>
          <Button variant="primary" disabled={!field || !reason.trim() || value === '' || blocked}
            loading={save.isPending} onClick={() => save.mutate()}>Save correction</Button>
        </>
      }
    >
      {row && (
        <div className="space-y-3">
          {pv && !pv.correctable && (
            <div className="rounded-control bg-warning-500/10 p-2 text-sm text-warning-600 dark:text-harvest-300">
              {blocks.fromCredit
                ? 'This is an applied account credit and cannot be edited. Cancel the order to release the credit, then re-apply.'
                : blocks.notApproved
                  ? 'This payment is not approved yet — approve or reject it instead of correcting it here.'
                  : (pv.reason ?? 'This entry cannot be corrected here.')}
            </div>
          )}
          {p && <div className="rounded-control bg-surface-2 p-2 text-sm">{p.paymentNo} · {p.retailerName} · {inr(p.amount)}{p.orderNo ? ` · ${p.orderNo}` : ''}</div>}

          {/* "Bank account" is not offered on a CASH payment: cash went through no account, and the
              service refuses to put one on it. If the money really was banked, the MODE is what is
              wrong — correct that first, and the account becomes a question worth asking. */}
          <Field label="What needs correcting?">
            <Select allowClear className="w-full" value={field} onChange={(v: any) => { setField(v); setValue(''); }}
              options={CORRECTABLE.filter((c) => c.value !== 'receivedBankAccountId' || !cashPayment)} />
          </Field>
          {cashPayment && (
            <p className="type-caption text-subtle">
              Recorded as cash, so it names no bank account. Correct the payment mode first if it was actually banked.
            </p>
          )}

          {field && (
            <>
              <div className="rounded-control border border-default p-2 text-sm">
                <div className="flex justify-between"><span className="text-subtle">Current</span><span>{String(current ?? '—')}</span></div>
              </div>
              <Field label="New value">
                {field === 'retailerId' ? (
                  <Select {...CUSTOMER_SELECT_PROPS} filterOption={false} onSearch={retailerPick.onSearch} className="w-full" value={value} onChange={setValue}
                    options={retailerPick.rows.map((r: any) => customerOption(r))} />
                ) : field === 'mode' ? (
                  <Select className="w-full" value={value} onChange={setValue} options={MODES.map((m) => ({ value: m, label: m }))} />
                ) : field === 'receivedBankAccountId' ? (
                  <Select className="w-full" value={value} onChange={setValue}
                    options={(Array.isArray(banks) ? banks : ((banks as any)?.data ?? [])).map((b: any) => ({ value: String(b.id), label: `${b.bankName} ····${String(b.accountNumber ?? '').slice(-4)}` }))} />
                ) : field === 'paymentDate' ? (
                  <Input type="date" value={value} onChange={(e: any) => setValue(e.target.value)} />
                ) : field === 'amount' ? (
                  <Input type="number" value={value} onChange={(e: any) => setValue(e.target.value)} />
                ) : (
                  <Input value={value} onChange={(e: any) => setValue(e.target.value)} />
                )}
              </Field>

              {partyBlocked && (
                <div className="rounded-control bg-warning-500/10 p-2 text-sm text-warning-600 dark:text-harvest-300">
                  This payment is already allocated ({inr(blocks.allocatedMinAmount)}). Remove or reverse its
                  allocations before changing the party.
                </div>
              )}
              {field === 'amount' && blocks.allocatedMinAmount > 0 && (
                <div className="text-xs text-muted">Cannot go below {inr(blocks.allocatedMinAmount)} — that much is already allocated.</div>
              )}

              <Field label="Reason (recorded in the audit log)">
                <Input value={reason} onChange={(e: any) => setReason(e.target.value)} placeholder="e.g. wrong retailer selected at entry" />
              </Field>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

function IgnoreDialog({ row, onClose, onConfirm }: { row: any | null; onClose: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal open={!!row} onOpenChange={(o) => { if (!o) { setReason(''); onClose(); } }} title="Ignore this statement row"
      footer={<>
        <Button variant="default" onClick={() => { setReason(''); onClose(); }}>Cancel</Button>
        <Button variant="primary" disabled={!reason.trim()} onClick={() => { onConfirm(reason.trim()); setReason(''); }}>Ignore row</Button>
      </>}>
      <div className="space-y-2">
        <div className="text-sm text-muted">The row stays on the statement and in the export — it is simply marked as deliberately not matched.</div>
        <Input value={reason} onChange={(e: any) => setReason(e.target.value)} placeholder="Reason (required)" />
      </div>
    </Modal>
  );
}

function CompleteDialog({ open, summary, onClose, onConfirm }: { open: boolean; summary: any; onClose: () => void; onConfirm: (note: string) => void }) {
  const [note, setNote] = useState('');
  const diff = Number(summary.difference ?? 0);
  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) { setNote(''); onClose(); } }} title="Complete this reconciliation"
      footer={<>
        <Button variant="default" onClick={() => { setNote(''); onClose(); }}>Cancel</Button>
        <Button variant="primary" disabled={diff !== 0 && !note.trim()} onClick={() => onConfirm(note.trim())}>Complete</Button>
      </>}>
      <div className="space-y-2 text-sm">
        <div className="grid grid-cols-2 gap-1">
          <span className="text-subtle">Matched</span><span>{(summary.exact ?? 0) + (summary.manual ?? 0) + (summary.corrected ?? 0)}</span>
          <span className="text-subtle">Reviewed bank-only</span><span>{summary.reviewedOnly ?? 0}</span>
          <span className="text-subtle">Ignored</span><span>{summary.ignored ?? 0}</span>
          <span className="text-subtle">In ERP but not on the statement</span><span>{summary.erpOnlyCount ?? 0}</span>
          <span className="text-subtle">Bank total</span><span>{inr((summary.bankCredits ?? 0) - (summary.bankDebits ?? 0))}</span>
          <span className="text-subtle">ERP total</span><span>{inr((summary.erpMoneyIn ?? 0) - (summary.erpMoneyOut ?? 0))}</span>
        </div>
        {diff !== 0 && (
          <div className="flex items-start gap-2 rounded-control bg-warning-500/10 p-2 text-warning-600 dark:text-harvest-300">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <div>The bank and ERP totals differ by <b>{inr(Math.abs(diff))}</b>. Every row has been reviewed. Enter a completion note to continue.</div>
          </div>
        )}
        <Input value={note} onChange={(e: any) => setNote(e.target.value)} placeholder={diff !== 0 ? 'Completion note (required)' : 'Completion note (optional)'} />
        <div className="text-xs text-muted">Once completed, this reconciliation is frozen — its rows can no longer be matched, reviewed or corrected.</div>
      </div>
    </Modal>
  );
}

/**
 * Apply one decision to every ERP-only entry still awaiting one. A month's statement routinely leaves
 * dozens of un-cleared cheques and out-of-period entries; requiring a click each is unusable, and the
 * volume of requests would trip the API rate limit.
 */
function BulkErpDialog({ open, pending, onClose, onConfirm }: { open: boolean; pending: any[]; onClose: () => void; onConfirm: (state: string, note: string) => void }) {
  const [state, setState] = useState<string>('PENDING_CLEARANCE');
  const [note, setNote] = useState('');
  const needsReason = state === 'IGNORED';
  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) { setNote(''); onClose(); } }} title={`Mark ${pending.length} ERP-only entr${pending.length === 1 ? 'y' : 'ies'}`}
      footer={<>
        <Button variant="default" onClick={() => { setNote(''); onClose(); }}>Cancel</Button>
        <Button variant="primary" disabled={needsReason && !note.trim()} onClick={() => onConfirm(state, note.trim())}>
          Apply to {pending.length}
        </Button>
      </>}>
      <div className="space-y-2">
        <div className="text-sm text-muted">
          These are ERP entries on this bank account that the statement never mentioned — usually cheques
          that have not cleared yet, or entries dated outside the statement period.
        </div>
        <Field label="Decision">
          <Select className="w-full" value={state} onChange={(v: any) => setState(v)} options={ERP_ONLY_STATES} />
        </Field>
        <Field label={needsReason ? 'Reason (required)' : 'Note (optional)'}>
          <Input value={note} onChange={(e: any) => setNote(e.target.value)} placeholder={needsReason ? 'Why are these being ignored?' : ''} />
        </Field>
      </div>
    </Modal>
  );
}

/**
 * A row-action menu that escapes the table's scroll container.
 *
 * `ui/Dropdown` renders its panel `absolute` inside the trigger's own stacking context. Every
 * TwDataTable sits in an `overflow-x-auto` wrapper, and CSS resolves `overflow-y` to `auto` whenever
 * `overflow-x` is, so the panel could not overflow the table — it just added a vertical scrollbar.
 * Radix Popover portals to the body, so the menu floats free.
 */
function RowMenu({ content, children }: { content: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      overlayClassName="max-w-none p-0"
      content={<div onClick={() => setOpen(false)}>{content}</div>}
    >
      {children}
    </Popover>
  );
}

/**
 * One titled group of statement lines in the right-hand pane.
 *
 * Grouped by what the reviewer must DO, not by internal status — a worklist, not a status dump. Each
 * card shows the bank line and the ERP entry we paired it with, plus the reason we paired them, so a
 * decision can be taken without leaving the card. Settled groups start collapsed so the work in hand
 * stays at the top of the pane.
 */
function Section({
  title, tone, lines, empty, hint, collapsed,
  cardRefs, selectedRow, select, act, canMatch, canCorrect, completed,
  setCorrectRow, setIgnoreRow, batchId, refresh,
}: any) {
  const [open, setOpen] = useState(!collapsed);
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  return (
    <Card>
      <button
        type="button"
        className="mb-1 flex w-full items-center gap-2 text-left"
        onClick={() => setOpen((o: boolean) => !o)}
      >
        <span className={`font-semibold ${tone}`}>{title}</span>
        <span className="text-xs text-muted">
          {lines.length}{lines.length ? ` · ${inr(lines.reduce((a: number, l: any) => a + Number(l.amount || 0), 0))}` : ''}
        </span>
        <span className="ml-auto text-xs text-subtle">{open ? 'Hide' : 'Show'}</span>
      </button>
      {hint && open && <div className="mb-2 text-xs text-subtle">{hint}</div>}
      {open && (
        !lines.length
          ? <div className="py-2 text-sm text-muted">{empty}</div>
          : (
            <div className="space-y-1.5">
              {lines.map((l: any) => (
                <div
                  key={l.id}
                  ref={(el) => { cardRefs.current[String(l.id)] = el; }}
                  onClick={() => select(String(l.id), 'right')}
                  className={`cursor-pointer rounded-control border p-2 text-xs ${ String(l.id) === selectedRow ? 'border-brand-500/50 bg-brand-500/5' : 'border-line hover:bg-surface-2'}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      {/* The bank's line */}
                      <div className="truncate font-medium text-content">
                        {formatIstDate(l.transactionDate)} · {l.narration || 'no narration'}
                      </div>
                      <div className="text-subtle">
                        {Number(l.creditAmount) > 0
                          ? <span className="text-brand-700 dark:text-brand-300">received {inr(l.creditAmount)}</span>
                          : <span className="text-warning-600 dark:text-harvest-300">paid {inr(l.debitAmount)}</span>}
                        {l.referenceNo ? ` · ref ${l.referenceNo}` : ''}
                      </div>

                      {/* What we paired it with, and why */}
                      {l.erp ? (
                        <div className="mt-1 rounded bg-surface-2 px-2 py-1">
                          <div className="truncate text-content">
                            ↔ {l.erp.docNo} · {l.erp.kind === 'COLLECTION'
                              ? <RetailerLink id={l.erp.retailerId}>{l.erp.partyName}</RetailerLink>
                              : <UserLink id={l.erp.partyUserId} role={l.erp.partyRole}>{l.erp.partyName}</UserLink>}
                            {l.erp.orderNo ? <> · <OrderLink id={l.erp.orderId}>{l.erp.orderNo}</OrderLink></> : null}
                          </div>
                          <div className="truncate text-subtle">
                            {formatIstDate(l.erp.date)} · {l.erp.mode ?? '—'}
                            {l.dateDiffers ? ' · date differs from the statement' : ''}
                          </div>
                          {l.matchReason && <div className="truncate text-subtle" title={l.matchReason}>{l.matchReason}</div>}
                        </div>
                      ) : (
                        <div className="mt-1 text-subtle">No ERP entry paired with this transaction.</div>
                      )}
                      {l.reviewNote && <div className="mt-1 text-subtle">Note: {l.reviewNote}</div>}
                      {l.ignoredReason && <div className="mt-1 text-subtle">Set aside: {l.ignoredReason}</div>}
                    </div>
                    <Tag tone={STATUS[l.matchStatus]?.tone ?? 'default'} style={{ margin: 0 }}>{STATUS[l.matchStatus]?.label ?? l.matchStatus}</Tag>
                  </div>

                  {/* Decisions live ON the card — the whole point of grouping by action.
                      Every one of them carries a BORDER (variant="default", never "text"): a
                      borderless button sitting under three lines of the card's own prose reads as
                      more prose, so "Mark reviewed" and "Set aside…" were indistinguishable from the
                      narration above them. `border-line-strong` because the card's tinted background
                      swallows the standard hairline. */}
                  {canMatch && !completed && (
                    <div className="mt-2 flex flex-wrap gap-1.5" onClick={(e) => e.stopPropagation()}>
                      {l.erp && ['POSSIBLE_MATCH', 'CONFLICT', 'UNMATCHED'].includes(l.matchStatus) && (
                        <Button size="sm" variant="primary" onClick={() => confirmAction({ title: 'Accept this suggested match?', content: 'The statement transaction and the ERP entry are marked reconciled against each other.', okText: 'Yes, accept' }, () => act.mutate({ url: `/rows/${l.id}/accept-match`, data: {} }))}>Accept</Button>
                      )}
                      <Button size="sm" className={CARD_ACTION} onClick={() => setPickerFor(pickerFor === String(l.id) ? null : String(l.id))}>
                        {pickerFor === String(l.id) ? 'Close' : (l.erp ? 'Change match' : 'Find a match')}
                      </Button>
                      {l.erp && <Button size="sm" className={CARD_ACTION} onClick={() => confirmAction({ title: 'Unmatch this transaction?', content: 'Both sides go back to unreconciled and can be matched again.', okText: 'Yes, unmatch', danger: true }, () => act.mutate({ url: `/rows/${l.id}/match`, method: 'DELETE' }))}>Unmatch</Button>}
                      <Button size="sm" className={CARD_ACTION} onClick={() => confirmAction({ title: l.reviewed ? 'Mark this transaction as not reviewed?' : 'Mark this transaction as reviewed?', okText: 'Yes' }, () => act.mutate({ url: `/rows/${l.id}/review`, data: { reviewed: !l.reviewed } }))}>
                        {l.reviewed ? 'Clear reviewed' : 'Mark reviewed'}
                      </Button>
                      <Button size="sm" className={CARD_ACTION} onClick={() => setIgnoreRow(l)}>Set aside…</Button>
                      {canCorrect && l.erp?.kind === 'COLLECTION' && (
                        <Button size="sm" className={CARD_ACTION} onClick={() => setCorrectRow(l)}>Correct…</Button>
                      )}
                    </div>
                  )}

                  {/* The picker lives INSIDE the card. A drawer would slide over the very comparison
                      the reviewer is making — you cannot choose between two entries while the line
                      you are choosing for is hidden behind the panel. */}
                  {pickerFor === String(l.id) && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <InlineCandidates
                        batchId={batchId}
                        row={l}
                        onMatched={() => { setPickerFor(null); act.mutate({ url: '', method: 'GET' } as any, { onSettled: () => {} }); }}
                        refresh={refresh}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
      )}
    </Card>
  );
}

/**
 * Candidate list for one statement line, rendered in place.
 *
 * Suggestions first with their score and the reason they were suggested, then everything else on the
 * account so a human can override. Each row states its own case — picking between two plausible
 * entries should not require going and reading the ERP.
 */
function InlineCandidates({ batchId, row, onMatched, refresh }: any) {
  const { data, isLoading } = useQuery({
    queryKey: ['recon-candidates', batchId, row.id],
    queryFn: () => request<any>({ url: `/bank-reconciliations/${batchId}/rows/${row.id}/candidates` }),
  });
  const pick = useConfirmedMutation({ title: "Match this statement transaction to the selected entry?", content: "Both are marked reconciled against each other.", okText: "Yes, match" }, {
    mutationFn: (e: any) => request({
      url: `/bank-reconciliations/${batchId}/rows/${row.id}/match`, method: 'POST',
      data: { sourceType: e.sourceType, sourceId: String(e.sourceId) },
    }),
    onSuccess: () => { message.success('Matched'); refresh(); onMatched(); },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not match'),
  });
  const d = (data as any)?.data ?? data;

  const Row = ({ e, suggested }: any) => (
    <button type="button" onClick={() => pick.mutate(e)}
      className="flex w-full items-start justify-between gap-2 rounded border border-line px-2 py-1.5 text-left hover:bg-brand-500/5">
      <span className="min-w-0">
        <span className="block truncate font-medium text-content">{e.docNo} · {e.partyName ?? '—'}</span>
        <span className="block truncate text-subtle">{formatIstDate(e.date)} · {e.mode ?? '—'} · {e.reference ?? 'no reference'}</span>
        {suggested && e.reason && <span className="block truncate text-subtle" title={e.reason}>{e.reason}</span>}
      </span>
      <span className="flex shrink-0 items-center gap-1.5">
        {suggested && typeof e.score === 'number' && (
          <Tag tone={e.score >= 70 ? 'green' : 'gold'} style={{ margin: 0 }}>{e.score}%</Tag>
        )}
        <b className="whitespace-nowrap">{inr(e.amount)}</b>
      </span>
    </button>
  );

  return (
    <div className="mt-2 rounded-control border border-line bg-surface-2 p-2 text-xs">
      {isLoading ? (
        <div className="flex items-center gap-2 py-2 text-muted"><Spinner /> Looking for candidates…</div>
      ) : (
        <>
          <div className="mb-1 type-overline text-subtle">Suggested — same amount and direction</div>
          {(d?.suggested ?? []).length
            ? <div className="space-y-1">{d.suggested.map((e: any) => <Row key={`s-${e.sourceType}-${e.sourceId}`} e={e} suggested />)}</div>
            : <div className="pb-1 text-muted">Nothing on this account matches that amount and direction.</div>}

          <div className="mb-1 mt-2 type-overline text-subtle">Anything else on this bank account</div>
          <div className="max-h-48 space-y-1 overflow-auto">
            {(d?.others ?? []).map((e: any) => <Row key={`o-${e.sourceType}-${e.sourceId}`} e={e} />)}
            {!(d?.others ?? []).length && <div className="text-muted">Nothing else is available on this account.</div>}
          </div>
        </>
      )}
    </div>
  );
}
